import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, Dialog, EmptyState, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";
import { FineTuningJobStatusPill } from "../../components/FineTuningJobStatusPill";
import { FineTuningMutationNotice, useMutationNotice } from "../../components/FineTuningMutationNotice";

type ResultRecord = Record<string, unknown>;

type FineTuningEval = {
  id: string;
  status: string;
  phase: string | null;
  org_id: string;
  deployment_id: string;
  test_file: string;
  scorer: string;
  // output transform applied before scoring; "none" = naked model. When set, metrics.raw carries
  // the naked-model numbers alongside the delivered (post-transform) ones.
  postprocessor?: string;
  served_model_name: string;
  n_examples: number;
  metrics: Record<string, any>;
  results_uri: string | null;
  results_sample?: ResultRecord[] | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
};

type EvalEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

const TERMINAL = new Set(["succeeded", "failed", "cancelled"]);
const POLL_MS = 6000;
// Platform-admin view across every org (the page is admin-only). Each row shows the owning org.
const ALL_ORGS = "all_orgs=true";

const COLUMNS = "1.3fr 1.4fr 1.2fr 1.2fr 0.9fr 0.7fr 1fr";

const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 14)}…` : id);

const pct = (v: unknown) => (typeof v === "number" ? `${(v * 100).toFixed(1)}%` : "—");
const fmt = (v: unknown) => (typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(3)) : String(v));

// the headline number shown in the list — pass_rate, or intent accuracy for the slot/intent scorer
const headline = (ev: FineTuningEval): string => {
  const m = ev.metrics || {};
  if (typeof m.pass_rate === "number") return pct(m.pass_rate);
  const inner = m.metrics || {};
  if (typeof inner.intent_accuracy === "number") return pct(inner.intent_accuracy);
  if (typeof inner.accuracy === "number") return pct(inner.accuracy);
  return "—";
};

export const AdminFineTuningEvalsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");

  const [evals, setEvals] = useState<FineTuningEval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvals = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      const { data } = await makeRequest<{ data: FineTuningEval[] }>(`/v1/fine_tuning/evaluations?${ALL_ORGS}&limit=200`);
      setEvals(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load evaluations");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvals(true);
  }, [loadEvals]);

  const hasActive = evals.some((e) => !TERMINAL.has(e.status));
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => loadEvals(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, loadEvals]);

  const selected = evals.find((e) => e.id === selectedId) || null;

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedId && "pr-[440px]")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Evals</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {evals.length} {evals.length === 1 ? "run" : "runs"} · all orgs
            </Text>
            {hasActive && <Spinner className="!h-4 !w-4 !text-brand" />}
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => loadEvals(true)}
          >
            <Icon
              name="refresh"
              size={18}
              className="text-brand"
            />
          </Button>
        </div>

        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Org", "Eval", "Scorer", "Model", "Status", "Result", "Created"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              {h}
            </span>
          ))}
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : evals.length === 0 ? (
            <EmptyState
              icon="checklist"
              title="No evaluations yet"
              description="Runs created via `vr ft eval` will appear here, across all organizations."
            />
          ) : (
            evals.map((ev) => (
              <div
                key={ev.id}
                onClick={() => setSearchParams({ id: ev.id })}
                className={clsx(
                  "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                  "hover:bg-gray-100 dark:hover:bg-gray-800",
                  selectedId === ev.id && "bg-gray-100 dark:bg-gray-800",
                )}
                style={{ gridTemplateColumns: COLUMNS }}
              >
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={ev.org_id}
                >
                  {ev.org_id}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm font-mono"
                  title={ev.id}
                >
                  {shortId(ev.id)}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm"
                  title={ev.scorer}
                >
                  {ev.scorer}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={ev.served_model_name}
                >
                  {ev.served_model_name || "—"}
                </span>
                <span className="py-3 pr-2">
                  <FineTuningJobStatusPill
                    status={ev.status}
                    phase={null}
                  />
                </span>
                <span className="py-3 pr-2 truncate text-sm font-medium">{headline(ev)}</span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(ev.created_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {selected && (
        <EvalDetailPanel
          summary={selected}
          allEvals={evals}
          onClose={() => setSearchParams({})}
          onChanged={() => loadEvals(false)}
        />
      )}
    </View>
  );
};

type EvalDetailPanelProps = {
  summary: FineTuningEval;
  allEvals: FineTuningEval[];
  onClose: () => void;
  onChanged: () => void;
};

const EvalDetailPanel = ({ summary, allEvals, onClose, onChanged }: EvalDetailPanelProps) => {
  // the list row lacks results_sample; fetch the full detail (and poll while active)
  const [ev, setEv] = useState<FineTuningEval>(summary);
  const [events, setEvents] = useState<EvalEvent[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const { notice, showError, showSuccess, clearNotice } = useMutationNotice();
  const idRef = useRef(summary.id);
  idRef.current = summary.id;

  // a notice belongs to the eval it was shown for — drop it when switching rows (keyed on the id:
  // the summary object itself gets a fresh identity on every list poll)
  useEffect(() => clearNotice(), [summary.id, clearNotice]);

  // a compare dialog belongs to the eval it was opened from
  useEffect(() => setIsCompareOpen(false), [summary.id]);

  const isTerminal = TERMINAL.has(ev.status);

  const load = useCallback(async () => {
    try {
      const [detail, eventsRes] = await Promise.all([
        makeRequest<FineTuningEval>(`/v1/fine_tuning/evaluations/${summary.id}?all_orgs=true`),
        makeRequest<{ data: EvalEvent[] }>(`/v1/fine_tuning/evaluations/${summary.id}/events?all_orgs=true`),
      ]);
      if (idRef.current !== summary.id) return; // stale after switching rows
      setEv(detail);
      setEvents(eventsRes.data);
    } catch {
      /* best-effort */
    }
  }, [summary.id]);

  useEffect(() => {
    setEv(summary);
    setEvents([]);
    load();
  }, [summary, load]);

  useEffect(() => {
    if (TERMINAL.has(ev.status)) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [ev.status, load]);

  const onCancel = async () => {
    setIsCancelling(true);
    clearNotice();
    try {
      await makeRequest(`/v1/fine_tuning/evaluations/${ev.id}?all_orgs=true`, { method: "DELETE" });
      showSuccess("Cancellation requested.");
      onChanged();
      load();
    } catch (e) {
      showError(e, "Failed to cancel the eval");
    } finally {
      setIsCancelling(false);
    }
  };

  const m = ev.metrics || {};
  const inner: Record<string, any> = m.metrics || {};
  const targets: Record<string, number> = m.targets || {};
  const latency = m.latency_ms || {};
  const confusion: Record<string, number> = inner.confusion || {};
  const sample = ev.results_sample || [];
  // When a post-processor ran, metrics.raw carries the naked-model numbers. `inner` (delivered,
  // post-transform) is what the targets gate on; we show `raw` beside it so the transform's effect
  // is visible — mirrors `vr ft eval`'s Raw column.
  const raw = m.raw || null;
  const rawInner: Record<string, any> = raw?.metrics || {};
  const hasPostproc = !!ev.postprocessor && ev.postprocessor !== "none";

  // scalar metrics (skip nested objects like confusion / per_class — shown separately)
  const scalarMetrics = Object.entries(inner).filter(([, v]) => typeof v !== "object");

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Evaluation</Text>
          <FineTuningJobStatusPill
            status={ev.status}
            phase={null}
          />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section
          label="Eval ID"
          value={ev.id}
          mono
        />
        <Section
          label="Organization"
          value={ev.org_id}
          mono
        />
        <Field
          label="Scorer"
          value={ev.scorer}
        />
        {hasPostproc && (
          <Field
            label="Post-processor"
            value={ev.postprocessor as string}
          />
        )}
        <Field
          label="Served model"
          value={ev.served_model_name || "—"}
        />
        <Section
          label="Deployment"
          value={ev.deployment_id}
          mono
        />
        <Field
          label="Test file"
          value={ev.test_file}
          mono
        />
        <Field
          label="Examples"
          value={String(ev.n_examples || 0)}
        />
        <Field
          label="Created"
          value={new Date(asUtc(ev.created_at)).toLocaleString()}
        />
        {ev.finished_at && (
          <Field
            label="Finished"
            value={new Date(asUtc(ev.finished_at)).toLocaleString()}
          />
        )}
        {ev.error && (
          <div>
            <Text className="block text-xs uppercase text-gray-400 mb-1">Error</Text>
            <Text className="text-sm text-error whitespace-pre-wrap break-words">{ev.error}</Text>
          </div>
        )}

        {/* Compare against another eval run — the retrain loop's before/after view */}
        {allEvals.some((e) => e.id !== ev.id) && (
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={() => setIsCompareOpen(true)}
          >
            <Text className="text-brand">Compare with…</Text>
          </Button>
        )}

        {/* Metrics — value vs target, PASS/FAIL */}
        {scalarMetrics.length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">Metrics{hasPostproc ? " · value = delivered, raw = naked model" : ""}</Text>
            <div className="space-y-1">
              {typeof m.pass_rate === "number" && (
                <MetricRow
                  name="pass_rate"
                  value={pct(m.pass_rate)}
                  raw={raw && typeof raw.pass_rate === "number" ? pct(raw.pass_rate) : undefined}
                />
              )}
              {scalarMetrics.map(([k, v]) => {
                const tgt = targets[k];
                const ok = typeof tgt === "number" && typeof v === "number" ? v >= tgt : undefined;
                const rawV = rawInner[k];
                return (
                  <MetricRow
                    key={k}
                    name={k}
                    value={fmt(v)}
                    raw={typeof rawV === "number" ? fmt(rawV) : undefined}
                    target={typeof tgt === "number" ? tgt.toFixed(2) : undefined}
                    pass={ok}
                  />
                );
              })}
            </div>
            {(latency.p50 != null || latency.p95 != null) && (
              <Text className="block text-xs text-gray-400 mt-2">
                latency p50 {latency.p50}ms · p95 {latency.p95}ms · errors {m.errors ?? 0}
              </Text>
            )}
          </div>
        )}

        {/* Confusion matrix (gold → pred mistakes) */}
        {Object.keys(confusion).length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-1">Confusion (gold → pred)</Text>
            <div className="space-y-0.5">
              {Object.entries(confusion).map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between text-xs font-mono"
                >
                  <span className="text-gray-600 dark:text-gray-300 break-all">{k}</span>
                  <span className="text-error">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-example sample (failures first) */}
        {sample.length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">
              Examples ({sample.length} shown{ev.results_uri ? ", failures first" : ""})
            </Text>
            <div className="space-y-2">
              {sample.map((r, i) => (
                <ExampleRow
                  key={i}
                  r={r}
                />
              ))}
            </div>
          </div>
        )}

        {/* Events */}
        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* outside the terminal guard so a success notice survives the status flipping terminal */}
      <FineTuningMutationNotice
        notice={notice}
        className="px-4 py-3"
      />

      {!isTerminal && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onCancel}
            isLoading={isCancelling}
          >
            <Text className="text-error">Cancel eval</Text>
          </Button>
        </div>
      )}

      {isCompareOpen && (
        <CompareEvalDialog
          current={ev}
          allEvals={allEvals}
          onClose={() => setIsCompareOpen(false)}
        />
      )}
    </div>
  );
};

const MetricRow = ({ name, value, raw, target, pass }: { name: string; value: string; raw?: string; target?: string; pass?: boolean }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <Text className="text-gray-600 dark:text-gray-300 font-mono text-xs">{name}</Text>
    <div className="flex items-center gap-2">
      <Text className="font-medium">{value}</Text>
      {raw != null && <Text className="text-xs text-gray-400">(raw {raw})</Text>}
      {target != null && <Text className="text-xs text-gray-400">/ {target}</Text>}
      {pass !== undefined && <Text className={clsx("text-xs font-semibold", pass ? "text-success" : "text-error")}>{pass ? "PASS" : "FAIL"}</Text>}
    </div>
  </div>
);

const ExampleRow = ({ r }: { r: ResultRecord }) => {
  const passed = r.passed === true;
  return (
    <div className="rounded-md border border-gray-100 dark:border-gray-800 p-2 text-xs">
      <div className="flex items-start gap-2">
        <span className={clsx("mt-0.5 font-bold flex-shrink-0", passed ? "text-success" : "text-error")}>{passed ? "✓" : "✗"}</span>
        <div className="min-w-0 space-y-0.5">
          <div className="break-words">
            <span className="text-gray-400">expected </span>
            <span className="font-mono">{String(r.expected ?? "")}</span>
          </div>
          <div className="break-words">
            <span className="text-gray-400">actual </span>
            <span className="font-mono">{String(r.actual ?? "")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

// --------------------------------------------------------------------------- eval compare
// Parity with `vr ft evals-compare`: flatten headline metrics into a delta table, and join the
// two evals' per-example rows on a canonical serialisation of the input messages to find
// pass/fail flips.

const RESULTS_PAGE_LIMIT = 500; // the /results endpoint's max page size
const RESULTS_ROW_CAP = 5000; // safety valve for enormous test sets
const FLIP_DISPLAY_CAP = 10;

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

// Flatten an eval's harness metrics into {name: number}: pass_rate, the scorer's scalar metrics,
// and latency percentiles as latency_ms_p50 / latency_ms_p95 / latency_ms_mean.
const flatMetrics = (ev: FineTuningEval): Record<string, number> => {
  const m = ev.metrics || {};
  const flat: Record<string, number> = {};
  if (isNum(m.pass_rate)) flat.pass_rate = m.pass_rate;
  Object.entries(m.metrics || {}).forEach(([k, v]) => {
    if (isNum(v)) flat[k] = v;
  });
  Object.entries(m.latency_ms || {}).forEach(([k, v]) => {
    if (isNum(v)) flat[`latency_ms_${k}`] = v;
  });
  return flat;
};

type MetricDelta = {
  key: string;
  a?: number;
  b?: number;
  delta?: number;
  improved?: boolean; // undefined when the delta is zero or a side is missing
};

const metricDeltas = (a: FineTuningEval, b: FineTuningEval): MetricDelta[] => {
  const fa = flatMetrics(a);
  const fb = flatMetrics(b);
  return Object.keys({ ...fa, ...fb })
    .sort()
    .map((key) => {
      const row: MetricDelta = { key, a: fa[key], b: fb[key] };
      if (row.a !== undefined && row.b !== undefined) {
        row.delta = row.b - row.a;
        // latency-named metrics are lower-is-better; everything else higher-is-better
        if (row.delta !== 0) row.improved = key.includes("latency") ? row.delta < 0 : row.delta > 0;
      }
      return row;
    });
};

const fmtMetric = (key: string, v?: number) => (v === undefined ? "—" : key === "pass_rate" ? pct(v) : fmt(v));

const fmtDelta = (key: string, d?: number) => {
  if (d === undefined) return "—";
  if (d === 0) return "0";
  const abs = key === "pass_rate" ? pct(Math.abs(d)) : fmt(Math.abs(d));
  return `${d > 0 ? "+" : "-"}${abs}`;
};

// Canonical JSON with recursively sorted object keys — the stable join key across two evals of
// the same test set (mirrors the CLI's sha256-of-canonical-JSON join; the string itself joins).
const stableStringify = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
  if (v && typeof v === "object") {
    const obj = v as Record<string, unknown>;
    const entries = Object.keys(obj)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`);
    return `{${entries.join(",")}}`;
  }
  const s = JSON.stringify(v);
  return s === undefined ? "null" : s;
};

// The input messages of a results row: the harness's `input` field, falling back to `messages`
// minus a trailing assistant (gold) turn — the same split the eval harness applies.
const resultInputMessages = (r: ResultRecord): unknown[] | null => {
  if (Array.isArray(r.input) && r.input.length > 0) return r.input;
  const raw = r.messages;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const last = raw[raw.length - 1] as Record<string, unknown> | null;
  const msgs = last && typeof last === "object" && last.role === "assistant" ? raw.slice(0, -1) : raw;
  return msgs.length > 0 ? msgs : null;
};

const inputKey = (r: ResultRecord): string | null => {
  const msgs = resultInputMessages(r);
  return msgs ? stableStringify(msgs) : null;
};

// A one-line preview of a row's input: the last user turn (or the last message)
const inputPreview = (r: ResultRecord, maxLen = 80): string => {
  const msgs = resultInputMessages(r) || [];
  let text = "";
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i] as Record<string, unknown> | null;
    if (m && typeof m === "object" && m.role === "user") {
      text = String(m.content ?? "");
      break;
    }
  }
  if (!text && msgs.length > 0) {
    const last = msgs[msgs.length - 1] as Record<string, unknown> | null;
    if (last && typeof last === "object") text = String(last.content ?? "");
  }
  text = text.split(/\s+/).join(" ").trim();
  return text.length > maxLen ? `${text.slice(0, maxLen - 1)}…` : text;
};

type ResultsPage = { data: ResultRecord[]; total?: number };

// Page through GET /evaluations/{id}/results until exhausted (or the safety cap)
const fetchAllResults = async (evalId: string): Promise<ResultRecord[]> => {
  const rows: ResultRecord[] = [];
  let offset = 0;
  for (;;) {
    const page = await makeRequest<ResultsPage>(`/v1/fine_tuning/evaluations/${evalId}/results?all_orgs=true&limit=${RESULTS_PAGE_LIMIT}&offset=${offset}`);
    const data = page.data || [];
    data.forEach((r) => rows.push(r));
    offset += data.length;
    if (data.length < RESULTS_PAGE_LIMIT || offset >= RESULTS_ROW_CAP || (typeof page.total === "number" && offset >= page.total)) {
      return rows;
    }
  }
};

type FlipEntry = {
  key: string;
  preview: string;
  expected: unknown;
  aActual: unknown;
  bActual: unknown;
};

type FlipSummary = {
  joined: number;
  regressions: FlipEntry[]; // passed in A, failed in B
  fixes: FlipEntry[]; // failed in A, passed in B
};

const computeFlips = (rowsA: ResultRecord[], rowsB: ResultRecord[]): FlipSummary => {
  const byB = new Map<string, ResultRecord>();
  rowsB.forEach((r) => {
    const k = inputKey(r);
    if (k && !byB.has(k)) byB.set(k, r);
  });
  const seenA = new Set<string>();
  let joined = 0;
  const regressions: FlipEntry[] = [];
  const fixes: FlipEntry[] = [];
  rowsA.forEach((ra) => {
    const k = inputKey(ra);
    if (!k || seenA.has(k)) return;
    seenA.add(k);
    const rb = byB.get(k);
    if (!rb) return;
    joined += 1;
    const pa = ra.passed === true;
    const pb = rb.passed === true;
    if (pa === pb) return;
    (pa ? regressions : fixes).push({
      key: k,
      preview: inputPreview(ra),
      expected: ra.expected,
      aActual: ra.actual,
      bActual: rb.actual,
    });
  });
  return { joined, regressions, fixes };
};

type CompareEvalDialogProps = {
  current: FineTuningEval; // B — the eval whose detail panel is open
  allEvals: FineTuningEval[];
  onClose: () => void;
};

const CompareEvalDialog = ({ current, allEvals, onClose }: CompareEvalDialogProps) => {
  const [other, setOther] = useState<FineTuningEval | null>(null); // A — the picked baseline
  const [flips, setFlips] = useState<FlipSummary | null>(null);
  const [flipsError, setFlipsError] = useState<string | null>(null);
  const [isLoadingFlips, setIsLoadingFlips] = useState(false);

  // candidates, same scorer + test set first (the list is already newest-first; the sort is stable)
  const rank = (e: FineTuningEval) => (e.scorer === current.scorer ? (e.test_file === current.test_file ? 0 : 1) : 2);
  const candidates = allEvals.filter((e) => e.id !== current.id).sort((x, y) => rank(x) - rank(y));

  useEffect(() => {
    if (!other) return;
    let stale = false;
    setFlips(null);
    setFlipsError(null);
    setIsLoadingFlips(true);
    Promise.all([fetchAllResults(other.id), fetchAllResults(current.id)])
      .then(([rowsA, rowsB]) => {
        if (!stale) setFlips(computeFlips(rowsA, rowsB));
      })
      .catch(() => {
        if (!stale) setFlipsError("Per-example results are unavailable for one of the evals — showing metric deltas only.");
      })
      .finally(() => {
        if (!stale) setIsLoadingFlips(false);
      });
    return () => {
      stale = true;
    };
  }, [other, current.id]);

  const deltas = other ? metricDeltas(other, current) : [];
  const mismatches = other
    ? [other.scorer !== current.scorer ? "scorer" : null, other.test_file !== current.test_file ? "test file" : null].filter((x): x is string => x !== null)
    : [];

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title="Compare evals"
      size="xl"
    >
      <View className="p-4 max-h-[70vh] overflow-y-auto space-y-4">
        {!other ? (
          candidates.length === 0 ? (
            <Text className="text-sm text-gray-400">No other evals to compare with.</Text>
          ) : (
            <div>
              <Text className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                Pick the baseline (A) to compare <span className="font-mono text-xs">{shortId(current.id)}</span> (B) against:
              </Text>
              <div className="space-y-1">
                {candidates.map((e) => (
                  <div
                    key={e.id}
                    onClick={() => setOther(e)}
                    className="flex items-center gap-3 rounded-md border border-gray-100 dark:border-gray-800 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <span
                      className="font-mono text-xs truncate"
                      title={e.id}
                    >
                      {shortId(e.id)}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{e.scorer}</span>
                    <span className="text-xs font-medium flex-shrink-0">{headline(e)}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{relativeTime(e.created_at)}</span>
                    <span className="flex-1" />
                    {e.scorer !== current.scorer && <span className="text-xs text-amber-600 dark:text-amber-400 flex-shrink-0">different scorer</span>}
                    {e.scorer === current.scorer && e.test_file !== current.test_file && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 flex-shrink-0">different test file</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                A <span className="font-mono">{shortId(other.id)}</span> (baseline) · B <span className="font-mono">{shortId(current.id)}</span> (this eval)
              </Text>
              <Button
                variant="ghost"
                type="button"
                onClick={() => setOther(null)}
              >
                <Text className="text-brand text-xs">Change baseline</Text>
              </Button>
            </div>

            {mismatches.length > 0 && (
              <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                <Text className="text-sm text-amber-800 dark:text-amber-200">
                  These evals differ in {mismatches.join(" and ")} — this is an apples-to-oranges comparison.
                </Text>
              </div>
            )}

            {/* Metric deltas */}
            <div>
              <Text className="block text-xs uppercase text-gray-400 mb-2">Metrics</Text>
              {deltas.length === 0 ? (
                <Text className="text-sm text-gray-400">Neither eval reports metrics yet.</Text>
              ) : (
                <div>
                  <div
                    className="grid border-b border-gray-200 dark:border-gray-700 pb-1 mb-1"
                    style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1fr" }}
                  >
                    {["Metric", "A", "B", "Δ"].map((h) => (
                      <span
                        key={h}
                        className="text-xs font-semibold text-gray-700 dark:text-gray-200"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                  {deltas.map((d) => (
                    <div
                      key={d.key}
                      className="grid items-baseline py-0.5"
                      style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1fr" }}
                    >
                      <span className="font-mono text-xs text-gray-600 dark:text-gray-300 truncate">{d.key}</span>
                      <span className="text-sm">{fmtMetric(d.key, d.a)}</span>
                      <span className="text-sm">{fmtMetric(d.key, d.b)}</span>
                      <span className={clsx("text-sm font-medium", d.improved === true && "text-success", d.improved === false && "text-error")}>
                        {fmtDelta(d.key, d.delta)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pass/fail flips (joined per-example results) */}
            <div>
              <Text className="block text-xs uppercase text-gray-400 mb-2">Pass/fail flips</Text>
              {isLoadingFlips ? (
                <View className="flex items-center gap-2 py-2">
                  <Spinner className="!h-4 !w-4 !text-brand" />
                  <Text className="text-sm text-gray-400">Loading per-example results…</Text>
                </View>
              ) : flipsError ? (
                <Text className="text-sm text-gray-400">{flipsError}</Text>
              ) : flips && flips.joined === 0 ? (
                <Text className="text-sm text-gray-400">
                  No per-example rows could be joined — the results lack input messages or the test sets don't overlap.
                </Text>
              ) : flips ? (
                <div className="space-y-3">
                  <Text className="text-sm text-gray-600 dark:text-gray-300">
                    {flips.joined} examples joined · <span className="text-error">{flips.regressions.length} regressed</span> ·{" "}
                    <span className="text-success">{flips.fixes.length} fixed</span>
                  </Text>
                  <FlipList
                    header="Regressions — passed in A, failed in B"
                    flips={flips.regressions}
                    tone="error"
                  />
                  <FlipList
                    header="Fixes — failed in A, passed in B"
                    flips={flips.fixes}
                    tone="success"
                  />
                </div>
              ) : null}
            </div>
          </>
        )}
      </View>
    </Dialog>
  );
};

const FlipList = ({ header, flips, tone }: { header: string; flips: FlipEntry[]; tone: "error" | "success" }) => {
  if (flips.length === 0) return null;
  return (
    <div>
      <Text className={clsx("block text-xs font-semibold mb-1", tone === "error" ? "text-error" : "text-success")}>
        {header} ({flips.length})
      </Text>
      <div className="space-y-2">
        {flips.slice(0, FLIP_DISPLAY_CAP).map((f) => (
          <div
            key={f.key}
            className="rounded-md border border-gray-100 dark:border-gray-800 p-2 text-xs space-y-0.5"
          >
            {f.preview && <div className="break-words text-gray-600 dark:text-gray-300">{f.preview}</div>}
            <div className="break-words">
              <span className="text-gray-400">expected </span>
              <span className="font-mono">{String(f.expected ?? "")}</span>
            </div>
            <div className="break-words">
              <span className="text-gray-400">A said </span>
              <span className="font-mono">{String(f.aActual ?? "")}</span>
            </div>
            <div className="break-words">
              <span className="text-gray-400">B said </span>
              <span className="font-mono">{String(f.bActual ?? "")}</span>
            </div>
          </div>
        ))}
        {flips.length > FLIP_DISPLAY_CAP && <Text className="block text-xs text-gray-400">+{flips.length - FLIP_DISPLAY_CAP} more</Text>}
      </div>
    </div>
  );
};
